import { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, BarChart2, MapPin, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useAuth } from '../contexts/AuthContext';
import { StudentEvolutionDashboard } from './StudentEvolutionDashboard';
export const ClassManagementCustomer = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [professors, setProfessors] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  
  const getAuthHeader = () => {
    const token = localStorage.getItem('tt_auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stdRes, clsRes, profRes] = await Promise.all([
          fetch(`${API_URL}/api/class-students`, { headers: getAuthHeader() }),
          fetch(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
          fetch(`${API_URL}/api/professors`, { headers: getAuthHeader() })
        ]);
        if (stdRes.ok) {
          const allStudents = await stdRes.json();
          // Filter students linked to the current user
          const normalizePhone = (p: string | undefined) => p ? p.replace(/\D/g, '') : '';
          const userPhone = normalizePhone(currentUser?.phone);

          const myStudents = currentUser ? allStudents.filter((s: any) => {
            // Vinculo via ID TechTennis (numericId)
            if (currentUser.numericId && s.numericId && String(s.numericId) === String(currentUser.numericId)) return true;
            
            // Vinculo via Telefone
            const sPhone = normalizePhone(s.phone);
            if (sPhone && userPhone && sPhone === userPhone) return true;
            
            return false;
          }) : [];
          setStudents(myStudents);
          if (myStudents.length > 0 && !selectedStudentId) {
            setSelectedStudentId(myStudents[0].id);
          }
        }
        if (clsRes.ok) setClasses(await clsRes.json());
        if (profRes.ok) {
           const data = await profRes.json();
           setProfessors(data.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })));
        }
      } catch (e) {
        console.error('Error fetching data', e);
      } finally {
        setIsLoading(false);
      }
    };
    if (currentUser) fetchData();
  }, [currentUser]);

  // State
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'agenda' | 'history' | 'evolution'>('agenda');
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().substring(0,7));
  const [evolutionLogs, setEvolutionLogs] = useState<any[]>([]);

  useEffect(() => {
    if (selectedStudentId) {
      fetch(`${API_URL}/api/evolution/student/${selectedStudentId}`, { headers: getAuthHeader() })
        .then(res => res.json())
        .then(data => setEvolutionLogs(data))
        .catch(e => console.error(e));
    }
  }, [selectedStudentId]);

  // Find active student context
  const activeStudent = students.find(s => s.id === selectedStudentId);
  const activeProfessor = activeStudent ? professors.find(p => p.id === activeStudent.professorId) : null;

  const getWeeklyHours = (studentClasses: any[]) => {
    if (!studentClasses || studentClasses.length === 0) return 1;
    const weeklySums: Record<string, number> = {};
    studentClasses.forEach(c => {
      const d = new Date(c.date + 'T00:00:00');
      const firstDay = new Date(d.getFullYear(), 0, 1);
      const pastDays = (d.getTime() - firstDay.getTime()) / 86400000;
      const weekNum = Math.ceil((pastDays + firstDay.getDay() + 1) / 7);
      
      const start = new Date(`1970-01-01T${c.timeStart}:00`).getTime();
      const end = new Date(`1970-01-01T${c.timeEnd}:00`).getTime();
      const hours = (end - start) / 3600000;
      weeklySums[weekNum] = (weeklySums[weekNum] || 0) + hours;
    });
    return Math.max(...Object.values(weeklySums)) || 1;
  };

  return (
    <div style={{ minHeight: '100vh', padding: '120px 5% 40px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-heading)' }}>
          Minhas Aulas
        </h1>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-secondary)' }}>Carregando dados...</div>
      ) : students.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-secondary)' }}>
          Nenhum registro de aulas encontrado para a sua conta. <br/>
          (Seu professor precisa cadastrar você com o seu ID TechTennis: #{currentUser?.numericId})
        </div>
      ) : students.length > 1 ? (
        <div className="glass-panel" style={{ padding: '24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Selecione o Professor/Turma:</label>
          <select 
            value={selectedStudentId} 
            onChange={(e) => setSelectedStudentId(e.target.value)}
            style={{ padding: '12px 16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', minWidth: '300px' }}
          >
            <option value="">Selecione...</option>
            {students.map(s => {
              const prof = professors.find(p => p.id === s.professorId);
              return (
                <option key={s.id} value={s.id}>{s.name} {prof ? `(Prof. ${prof.name})` : ''}</option>
              );
            })}
          </select>
        </div>
      ) : null}

      {activeStudent ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel" style={{ background: 'var(--bg-panel-solid)', borderRadius: '24px', overflow: 'hidden' }}>
          
          <div style={{ padding: '24px 40px', background: 'linear-gradient(to right, #483375, #2D1E4B)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
             <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: '24px' }}>Olá, {activeStudent.name}</h2>
                <div style={{ color: '#60A5FA', fontSize: '14px' }}>Professor(a): {activeProfessor?.name || 'Desconhecido'}</div>
             </div>
             <div style={{ textAlign: 'right' }}>
               <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.8 }}>Valor/Hora</div>
               <div style={{ fontSize: '20px', fontWeight: 800, color: '#10B981' }}>{activeStudent.hourlyRate ? `R$ ${Number(activeStudent.hourlyRate).toFixed(2)}` : 'À Combinar'}</div>
             </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '32px', padding: '16px 40px', borderBottom: '1px solid #E5E7EB', background: '#FFFFFF' }}>
            <div onClick={() => setActiveTab('agenda')} style={{ fontSize: '16px', fontWeight: activeTab === 'agenda' ? 800 : 600, color: activeTab === 'agenda' ? '#483375' : '#60A5FA', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} /> Aulas Agendadas
            </div>
            <div onClick={() => setActiveTab('history')} style={{ fontSize: '16px', fontWeight: activeTab === 'history' ? 800 : 600, color: activeTab === 'history' ? '#483375' : '#60A5FA', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChart2 size={18} /> Histórico e Extrato
            </div>
            <div onClick={() => setActiveTab('evolution')} style={{ fontSize: '16px', fontWeight: activeTab === 'evolution' ? 800 : 600, color: activeTab === 'evolution' ? '#483375' : '#60A5FA', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={18} /> Meu Desempenho
            </div>
          </div>

          <div style={{ padding: '40px', background: '#F8F9FA', minHeight: '500px' }}>
            
            {activeTab === 'agenda' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
               <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontSize: '20px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><Calendar size={20} color="#2563EB"/> Próximas Aulas</h3>
                  </div>

                  {classes.filter((c: any) => c.studentId === activeStudent.id && c.status === 'planned').length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280', background: 'white', borderRadius: '16px', border: '1px dashed #D1D5DB' }}>Nenhuma aula agendada para o futuro.</div>
                  ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '24px' }}>
                          {classes.filter((c: any) => c.studentId === activeStudent.id && c.status === 'planned').sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((cls: any) => (
                             <div key={cls.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', padding: '24px', borderRadius: '16px', borderLeft: '4px solid #60A5FA', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                               <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                 <div style={{ background: 'rgba(96,165,250,0.1)', color: '#2563EB', padding: '12px 20px', borderRadius: '12px', textAlign: 'center' }}>
                                   <div style={{ fontSize: '13px', textTransform: 'uppercase', fontWeight: 700 }}>{new Date(`${cls.date}T12:00:00`).toLocaleDateString('pt-BR', { month: 'short' })}</div>
                                   <div style={{ fontSize: '24px', fontWeight: 800 }}>{cls.date.split('-')[2]}</div>
                                 </div>
                                 <div>
                                   <div style={{ fontSize: '18px', fontWeight: 700, color: '#483375', marginBottom: '4px' }}>{cls.timeStart} às {cls.timeEnd}</div>
                                   <div style={{ fontSize: '14px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '6px' }}><MapPin size={14}/> {cls.location || 'Local não informado'}</div>
                                 </div>
                               </div>
                               <div style={{ background: 'rgba(96,165,250,0.1)', color: '#2563EB', padding: '6px 12px', borderRadius: '100px', fontSize: '13px', fontWeight: 700 }}>
                                 Planejada
                               </div>
                             </div>
                           ))}
                      </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'history' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-dark)', margin: 0 }}>Histórico do Mês</h3>
                  <select 
                    value={reportMonth} 
                    onChange={e => setReportMonth(e.target.value)}
                    style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #E5E7EB', background: 'white' }}>
                    <option value="2026-03">Março 2026</option>
                    <option value="2026-04">Abril 2026</option>
                    <option value="2026-05">Maio 2026</option>
                  </select>
                </div>

                {/* Dashboard Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                  {(() => {
                    const monthClasses = classes.filter(c => c.studentId === activeStudent.id && c.date.startsWith(reportMonth));
                    const completed = monthClasses.filter(c => c.status === 'completed');
                    const hours = completed.reduce((acc, c) => acc + ((new Date(`1970-01-01T${c.timeEnd}:00`).getTime() - new Date(`1970-01-01T${c.timeStart}:00`).getTime()) / 3600000), 0);
                    
                    let totalCost = 0;
                    if (activeStudent.pricingMode === 'fixed') {
                      if (activeStudent.fixedMonthlyHours) {
                        totalCost = Number(activeStudent.fixedMonthlyHours) * (Number(activeStudent.hourlyRate) || 0);
                      } else {
                        const studentClasses = classes.filter(c => c.studentId === activeStudent.id && c.date.startsWith(reportMonth));
                        const weeklyHours = getWeeklyHours(studentClasses);
                        totalCost = 4 * weeklyHours * (Number(activeStudent.hourlyRate) || 0);
                      }
                    } else {
                      totalCost = activeStudent.hourlyRate ? hours * Number(activeStudent.hourlyRate) : 0;
                    }

                    return (
                      <>
                        <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '24px', borderRadius: '16px' }}>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>Aulas Realizadas</h4>
                          <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>{completed.length}</div>
                        </div>
                        <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '24px', borderRadius: '16px' }}>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>Total de Horas</h4>
                          <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>{hours.toFixed(1)}h</div>
                        </div>
                        <div style={{ background: '#10B981', color: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 10px 20px rgba(16,185,129,0.2)' }}>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Acerto do Mês</h4>
                          <div style={{ fontSize: '28px', fontWeight: 800, whiteSpace: 'nowrap' }}>R$ {totalCost.toFixed(2)}</div>
                        </div>
                        {(() => {
                          const repBalance = classes.filter(c => c.studentId === activeStudent.id).reduce((acc, c) => {
                            if (!c.timeStart || !c.timeEnd) return acc;
                            const h = (new Date(`1970-01-01T${c.timeEnd}:00`).getTime() - new Date(`1970-01-01T${c.timeStart}:00`).getTime()) / 3600000;
                            if ((c.status === 'rain' || c.status.includes('cancelled')) && c.willHaveReplacement) return acc + h;
                            if (c.status === 'replacement') return acc - h;
                            return acc;
                          }, 0);
                          return (
                            <div style={{ background: '#F59E0B', color: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 10px 20px rgba(245,158,11,0.2)' }}>
                              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Horas para Reposição</h4>
                              <div style={{ fontSize: '28px', fontWeight: 800, whiteSpace: 'nowrap' }}>{repBalance.toFixed(1)} hr</div>
                            </div>
                          );
                        })()}
                      </>
                    )
                  })()}
                </div>

                {/* Listing */}
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontSize: '13px', textTransform: 'uppercase' }}>
                            <th style={{ padding: '16px 24px' }}>Data</th>
                            <th style={{ padding: '16px 24px' }}>Horário</th>
                            <th style={{ padding: '16px 24px' }}>Status</th>
                            <th style={{ padding: '16px 24px', textAlign: 'right' }}>Cobrança</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classes.filter(c => c.studentId === activeStudent.id && c.date.startsWith(reportMonth) && c.status !== 'planned')
                                  .sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                                  .map(cls => {
                            
                            const start = new Date(`1970-01-01T${cls.timeStart}:00`);
                            const end = new Date(`1970-01-01T${cls.timeEnd}:00`);
                            const hours = (end.getTime() - start.getTime()) / 3600000;
                            const value = (activeStudent.pricingMode !== 'fixed' && activeStudent.hourlyRate && cls.status === 'completed') ? hours * Number(activeStudent.hourlyRate) : 0;
                            
                            let statusConfig = { color: '#6B7280', bg: '#F3F4F6', label: 'Planejada' };
                            if (cls.status === 'completed') statusConfig = { color: '#10B981', bg: 'rgba(16,185,129,0.1)', label: 'Realizada' };
                            if (cls.status === 'replacement') statusConfig = { color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', label: 'Reposição' };
                            if (cls.status === 'rain') statusConfig = { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', label: 'Chuva' + (cls.willHaveReplacement ? ' (+Repos)' : '') };
                            if (cls.status.includes('cancelled')) statusConfig = { color: '#EF4444', bg: 'rgba(239,68,68,0.1)', label: 'Cancelada' + (cls.willHaveReplacement ? ' (+Repos)' : '') };

                            return (
                              <tr key={cls.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: cls.status !== 'completed' ? 0.7 : 1, color: '#2D1E4B' }}>
                                <td style={{ padding: '16px 24px', fontWeight: 600, color: '#2D1E4B' }}>{cls.date.split('-').reverse().join('/')}</td>
                                <td style={{ padding: '16px 24px', color: '#4B5563' }}>{cls.timeStart} às {cls.timeEnd}</td>
                                <td style={{ padding: '16px 24px' }}>
                                  <span style={{ background: statusConfig.bg, color: statusConfig.color, padding: '4px 12px', borderRadius: '100px', fontSize: '13px', fontWeight: 600 }}>
                                    {statusConfig.label}
                                  </span>
                                </td>
                                <td style={{ padding: '16px 24px', textAlign: 'right', fontWeight: 700, color: cls.status === 'completed' ? '#10B981' : '#6B7280' }}>
                                  {activeStudent.pricingMode === 'fixed' ? 'Fixo' : `R$ ${value.toFixed(2)}`}
                                </td>
                              </tr>
                            )
                          })}
                          {classes.filter(c => c.studentId === activeStudent.id && c.date.startsWith(reportMonth) && c.status !== 'planned').length === 0 && (
                            <tr>
                              <td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Nenhum histórico encontrado para o mês selecionado.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                </div>

              </motion.div>
            )}

            {activeTab === 'evolution' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-dark)', margin: 0 }}>Meu Desempenho (ATP KPIs)</h3>
                </div>

                {evolutionLogs.length === 0 ? (
                   <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280', background: 'white', borderRadius: '16px', border: '1px dashed #D1D5DB' }}>Seu professor ainda não registrou nenhuma avaliação de desempenho.</div>
                ) : (
                  <StudentEvolutionDashboard evolutionLogs={evolutionLogs} studentLevel={activeStudent?.level} />
                )}
              </motion.div>
            )}

          </div>

        </motion.div>
      ) : (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-secondary)' }}>
          Por favor, selecione sua conta no painel acima para visualizar suas aulas.
        </div>
      )}

    </div>
  );
};
